'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreatePlayer, useUpdatePlayer } from '@/hooks/use-director-data';
import type { PlayerSummary } from '@/hooks/use-director-data';

interface PlayerFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  player?: PlayerSummary | null;
  onSuccess?: () => void;
}

const LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'tournament', label: 'Tournament' },
];

const BALL_LEVELS = [
  { value: 'red', label: 'Red Ball', color: 'bg-red-500' },
  { value: 'orange', label: 'Orange Ball', color: 'bg-orange-500' },
  { value: 'green', label: 'Green Ball', color: 'bg-green-500' },
  { value: 'yellow', label: 'Yellow Ball', color: 'bg-yellow-400' },
];

export function PlayerFormModal({
  open,
  onOpenChange,
  player,
  onSuccess,
}: PlayerFormModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [level, setLevel] = useState('');
  const [ballLevel, setBallLevel] = useState('');
  const createPlayer = useCreatePlayer();
  const updatePlayer = useUpdatePlayer();

  const isEditing = !!player;
  const isSubmitting = createPlayer.isPending || updatePlayer.isPending;

  useEffect(() => {
    if (player) {
      setFirstName(player.firstName);
      setLastName(player.lastName);
      setDateOfBirth(player.dateOfBirth || '');
      setLevel(player.skillLevel || '');
      setBallLevel(player.ballLevel || '');
    } else {
      setFirstName('');
      setLastName('');
      setDateOfBirth('');
      setLevel('');
      setBallLevel('');
    }
  }, [player, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEditing && player) {
        await updatePlayer.mutateAsync({
          id: player.id,
          firstName,
          lastName,
          dateOfBirth: dateOfBirth || undefined,
          level: level || undefined,
          ballLevel: ballLevel || undefined,
        });
      } else {
        await createPlayer.mutateAsync({
          firstName,
          lastName,
          dateOfBirth: dateOfBirth || undefined,
          level: level || undefined,
          ballLevel: ballLevel || undefined,
        });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to save player:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Player' : 'Add New Player'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Emma"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Johnson"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dateOfBirth">Date of Birth</Label>
            <Input
              id="dateOfBirth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="level">Skill Level</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ballLevel">Ball Level</Label>
              <Select value={ballLevel} onValueChange={setBallLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select ball level" />
                </SelectTrigger>
                <SelectContent>
                  {BALL_LEVELS.map((bl) => (
                    <SelectItem key={bl.value} value={bl.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${bl.color}`} />
                        {bl.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Player'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
